
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { BarChart3, UploadCloud, AlertTriangle, Clock } from 'lucide-react';
import { StrategyListItem } from './types';
import { formatPercentage } from '@/lib/utils';

interface StrategySelectorListProps {
  strategies: StrategyListItem[];
  selectedStrategyId: string | null;
  onSelectStrategy: (id: string, source: 'internal' | 'external') => void;
  isExpanded: boolean;
}

export const StrategySelectorList: React.FC<StrategySelectorListProps> = ({
  strategies,
  selectedStrategyId,
  onSelectStrategy,
  isExpanded,
}) => {

  const getStatusBadge = (strategy: StrategyListItem) => {
     // Check if this is a VM strategy that hasn't started trading yet
     const hasStartedTrading = (strategy as any).backtestSettings?.hasStartedTrading;
     const isVMStrategy = strategy.name !== "Demo Strategy (Sample)" && strategy.source === 'internal';
     
     if (isVMStrategy && !hasStartedTrading) {
         return <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">
           <Clock className="h-3 w-3 mr-1"/>Deployed
         </div>;
     }
     if (strategy.status === 'pending_analysis') {
         return <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs bg-gray-200 text-gray-700">
           <Clock className="h-3 w-3 mr-1"/>Pending
         </div>;
     }
     if (strategy.status === 'error') {
        return <div className="flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs bg-red-100 text-red-700">
          <AlertTriangle className="h-3 w-3 mr-1"/>Error
        </div>;
     }
     if (strategy.isActive === false && strategy.source === 'internal') {
        return <div className="px-1.5 py-0.5 rounded-full text-xs border border-gray-300 text-gray-700">
          Inactive
        </div>;
     }
     // Show "Trading" for strategies that have started trading
     if (isVMStrategy && hasStartedTrading) {
         return <div className="px-1.5 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
           Trading
         </div>;
     }
     // Default to active for demo strategies or external
     if (strategy.source === 'internal' || strategy.status === 'active') {
         return <div className="px-1.5 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
           Demo
         </div>;
     }
     return null; // Should not happen if status is always set for external
  };


  return (
    <Card className={`transition-all duration-300 ease-in-out ${isExpanded ? 'h-auto' : 'h-[260px]'}`}>
      <CardContent className={`p-3 ${isExpanded ? '' : 'overflow-hidden'}`}>
        {strategies.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center p-4">
            <BarChart3 className="h-10 w-10 text-gray-300 mb-3" />
            <p className="text-sm text-gray-500">No strategies yet.</p>
            <p className="text-xs text-gray-400 mt-1">Create or import a strategy to begin.</p>
          </div>
        ) : (
          <ScrollArea className={isExpanded ? "h-auto" : "h-[230px]"}>
            <div className="space-y-2 pr-2">
              {strategies.map((strategy) => (
                <div
                  key={strategy.id}
                  className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedStrategyId === strategy.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                  onClick={() => onSelectStrategy(strategy.id, strategy.source)}
                >
                  <div className="flex items-center gap-2 overflow-hidden">
                    {strategy.source === 'external' ? (
                      <UploadCloud className="h-4 w-4 text-purple-500 flex-shrink-0" />
                    ) : (
                      <BarChart3 className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate" title={strategy.name}>{strategy.name}</p>
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                         <span>{strategy.source === 'external' ? `External (${strategy.fileType?.toUpperCase()})` : 'Internal'}</span>
                         {getStatusBadge(strategy)}
                      </div>
                    </div>
                  </div>
                  {strategy.ytdReturn !== undefined && (
                    <span className={`text-sm font-semibold ml-2 ${strategy.ytdReturn >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatPercentage(strategy.ytdReturn)}
                    </span>
                  )}
                   {strategy.status === 'pending_analysis' && <Clock className="h-4 w-4 text-gray-400 ml-2 flex-shrink-0" />}
                   {strategy.status === 'error' && <AlertTriangle className="h-4 w-4 text-red-400 ml-2 flex-shrink-0" />}
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
};
